import { Table } from '@ospace/shared'
import { ColumnDef } from '@tanstack/react-table'
import { Link } from 'react-router-dom'

import { deduplicationKindConfig, type DeduplicationKind } from '../config'
import type { RunSummary } from '../types'
import { formatDateTime, formatDuration } from '../utils/format'
import { runDetailPath } from '../utils/paths'
import { RunStatusBadge } from './StatusBadge'

const buildColumns = (kind: DeduplicationKind): ColumnDef<RunSummary>[] => [
  {
    accessorKey: 'startedAt',
    header: 'Started',
    cell: (info) => (
      <Link
        to={runDetailPath(kind, info.row.original.runId)}
        className='text-dark fw-bolder text-hover-primary'
      >
        {formatDateTime(info.getValue() as string)}
      </Link>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: (info) => <RunStatusBadge status={info.getValue() as RunSummary['status']} />,
  },
  {
    accessorKey: 'triggerType',
    header: 'Trigger',
    cell: (info) => (info.getValue() as string).toUpperCase(),
  },
  {
    id: 'progress',
    header: 'Groups',
    cell: (info) => {
      const run = info.row.original
      return `${run.groupsProcessed}${run.groupsTotal != null ? ` / ${run.groupsTotal}` : ''}`
    },
  },
  {
    id: 'failed',
    header: 'Failed',
    cell: (info) => {
      const run = info.row.original
      const failed = run.groupsFailed ?? run.statsJson?.groupsFailed ?? 0
      return failed > 0 ? failed : '—'
    },
  },
  {
    accessorKey: 'durationMs',
    header: 'Duration',
    cell: (info) => formatDuration(info.getValue() as number | null),
  },
]

type Props = {
  kind: DeduplicationKind
  runs: RunSummary[]
  isLoading?: boolean
}

export const RunsTable = ({ kind, runs, isLoading }: Props) => {
  const kindConfig = deduplicationKindConfig[kind]

  return (
    <div className='card'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title'>
          <span className='card-label fw-bolder fs-3'>{kindConfig.title} runs</span>
        </h3>
      </div>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <Table columns={buildColumns(kind)} data={runs} isLoading={isLoading} hidePagination />
        </div>
      </div>
    </div>
  )
}
